import { useEffect, useState } from 'react';
import { fetchIncassoDossierPreview, postIncassoOverdracht, type IncassoDossierPreview } from '../../lib/invoices';
import { formatCurrency } from '../../lib/format';

interface IncassoOverdrachtDialogProps {
  /** invoice sf_id; null = dicht */
  invoiceId: string | null;
  invoiceName: string;
  onClose: () => void;
  /** Na succesvolle overdracht (lijst verversen). */
  onTransferred: () => void;
}

/**
 * Preview + bevestiging van de incasso-overdracht. Toont exact de mail die
 * naar Bos Incasso gaat (dossier, tijdlijn, bijlage) — de menselijke gate
 * vóór de enige naar-buiten-tredende stap van het incasso-domein.
 */
export function IncassoOverdrachtDialog({ invoiceId, invoiceName, onClose, onTransferred }: IncassoOverdrachtDialogProps) {
  const [preview, setPreview] = useState<IncassoDossierPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toelichting, setToelichting] = useState('');
  // Preview ververst pas als de toelichting even niet wijzigt (debounce).
  const [toelichtingVoorPreview, setToelichtingVoorPreview] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setToelichtingVoorPreview(toelichting), 500);
    return () => clearTimeout(timer);
  }, [toelichting]);

  useEffect(() => {
    if (!invoiceId) {
      setPreview(null);
      setError(null);
      setToelichting('');
      setToelichtingVoorPreview('');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchIncassoDossierPreview(invoiceId, toelichtingVoorPreview)
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [invoiceId, toelichtingVoorPreview]);

  if (!invoiceId) return null;

  async function verstuur() {
    if (!invoiceId) return;
    setSending(true);
    setError(null);
    try {
      await postIncassoOverdracht(invoiceId, toelichting);
      onTransferred();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Overdracht mislukt');
    } finally {
      setSending(false);
    }
  }

  const blokkade = preview?.bijlageFout
    ? `Factuur-PDF kan niet mee: ${preview.bijlageFout}`
    : preview?.dossier.bereikbaarheid?.grond
      ? `Niet overdragen: ${preview.dossier.bereikbaarheid.omschrijving ?? preview.dossier.bereikbaarheid.grond}`
      : null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Overdragen aan Bos Incasso</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {invoiceName}
            {preview ? ` · ${preview.dossier.schuldenaar.naam} · ${formatCurrency(preview.dossier.factuur.openstaand)} openstaand` : ''}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {loading ? (
            <div className="space-y-2 animate-pulse">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-4 bg-slate-100 rounded" />
              ))}
            </div>
          ) : error && !preview ? (
            <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-sm text-red-700">{error}</div>
          ) : preview ? (
            <>
              <div className="text-sm text-slate-700">
                <span className="font-medium">Aan:</span> {preview.mail.aan} ·{' '}
                <span className="font-medium">cc:</span> {preview.mail.cc} ·{' '}
                <span className="font-medium">bijlagen:</span>{' '}
                {preview.bijlagen?.length ? preview.bijlagen.join(', ') : preview.bijlage ?? 'geen'}
              </div>
              {preview.dossier.waarschuwingen.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-xs text-amber-800">
                  {preview.dossier.waarschuwingen.map((w, i) => (
                    <div key={i}>{w}</div>
                  ))}
                </div>
              )}
              {blokkade && (
                <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-xs text-red-700">{blokkade}</div>
              )}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Extra toelichting voor Bos (optioneel, komt als eigen blok in de mail)
                </label>
                <textarea
                  value={toelichting}
                  onChange={(e) => setToelichting(e.target.value)}
                  rows={3}
                  placeholder="Bijvoorbeeld: klant heeft telefonisch toegezegd maar komt afspraken niet na."
                  className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-3 whitespace-pre-wrap font-mono text-slate-800">
                {`Onderwerp: ${preview.mail.subject}\n\n${preview.mail.text}`}
              </pre>
            </>
          ) : null}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-3">
          <div className="text-xs text-red-700 flex-1">{error && preview ? error : ''}</div>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-sm font-medium rounded-md text-slate-600 hover:bg-slate-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            disabled={sending || loading || !preview || !!blokkade || preview.incassoStatus !== 'kandidaat'}
            onClick={verstuur}
            className="px-3 py-2 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {sending ? 'Versturen…' : 'Verstuur naar Bos Incasso'}
          </button>
        </div>
      </div>
    </div>
  );
}
